import React, { useEffect, useMemo, useRef, useState } from "react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const TOKEN_KEY = "ct_login_token";
const REFRESH_SEC = 60;
const RUNNING_KEY = "dhan_expiry_writer_matrix_running_v1";
const ACTIVE_INDEX_KEY = "dhan_expiry_writer_matrix_active_index_v1";
const DHAN_CREDENTIALS_KEY = "dhan_expiry_writer_matrix_credentials_v1";

const INDEX_TABS = [
  { id: "nifty", label: "NIFTY", gap: 50 },
  { id: "sensex", label: "SENSEX", gap: 100 },
  { id: "banknifty", label: "BANKNIFTY", gap: 100 },
  { id: "bankex", label: "BANKEX", gap: 100 },
];

const C = {
  panel: "#050806",
  card: "#080f1a",
  border: "#f7c94855",
  gold: "#ffd84a",
  dim: "#9f7a24",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
};

function num(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function fmt(value) {
  return num(value).toLocaleString("en-IN");
}

function fmtSigned(value) {
  const next = num(value);
  if (!next) return "0";
  return `${next > 0 ? "+" : ""}${fmt(next)}`;
}

function fmtCr(value) {
  const next = num(value);
  if (!next) return "0";
  return `${(next / 10000000).toFixed(2)} Cr`;
}

function fmtSignedCr(value) {
  const next = num(value);
  if (!next) return "0";
  return `${next > 0 ? "+" : ""}${(next / 10000000).toFixed(2)} Cr`;
}

function readJson(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
}

function readRunningState() {
  return readJson(RUNNING_KEY, {});
}

function readCredentials() {
  return readJson(DHAN_CREDENTIALS_KEY, { clientId: "", accessToken: "" });
}

function normalizeRows(chain) {
  const calls = (chain?.calls || []).map(row => ({
    side: "CE",
    strike: num(row.strike ?? row.strikePrice),
    oi: num(row.oi ?? row.openInterest),
    oiChange: num(row.oiChange ?? row.changeinOpenInterest ?? row.changeInOI),
    volume: num(row.volume ?? row.totalTradedVolume),
  }));

  const puts = (chain?.puts || []).map(row => ({
    side: "PE",
    strike: num(row.strike ?? row.strikePrice),
    oi: num(row.oi ?? row.openInterest),
    oiChange: num(row.oiChange ?? row.changeinOpenInterest ?? row.changeInOI),
    volume: num(row.volume ?? row.totalTradedVolume),
  }));

  return { calls, puts };
}

function nearestStrike(strikes, spot) {
  if (!strikes.length) return 0;
  return strikes.reduce((best, strike) =>
    Math.abs(strike - spot) < Math.abs(best - spot) ? strike : best
  );
}

function pickRowsByStrikes(rows, strikes) {
  const rowMap = new Map(rows.map(row => [row.strike, row]));
  return strikes.map(strike => rowMap.get(strike)).filter(Boolean);
}

function calculateSpotMetrics(chain, spot, oiData = null) {
  const { calls, puts } = normalizeRows(chain);
  const strikes = [...new Set([...calls, ...puts].map(row => row.strike))]
    .filter(Boolean)
    .sort((a, b) => a - b);

  if (!strikes.length) {
    return {
      spot,
      atm: oiData?.atm || 0,
      ceTopItmVolume: 0,
      peTopItmVolume: 0,
      callSellerOi: num(oiData?.totalCeOI),
      callSellerChange: (oiData?.calls || []).reduce((sum, row) => sum + num(row.oiChange), 0),
      putSellerOi: num(oiData?.totalPeOI),
      putSellerChange: (oiData?.puts || []).reduce((sum, row) => sum + num(row.oiChange), 0),
      callSellerStrikes: (oiData?.calls || []).map(row => row.strike),
      putSellerStrikes: (oiData?.puts || []).map(row => row.strike),
      ceItmStrikes: [],
      peItmStrikes: [],
    };
  }

  const atm = oiData?.atm || nearestStrike(strikes, spot);

  const ceItmStrikes = strikes
    .filter(strike => strike <= spot)
    .sort((a, b) => b - a)
    .slice(0, 10);

  const peItmStrikes = strikes
    .filter(strike => strike >= spot)
    .sort((a, b) => a - b)
    .slice(0, 10);

  const ceTopItmRows = pickRowsByStrikes(calls, ceItmStrikes);
  const peTopItmRows = pickRowsByStrikes(puts, peItmStrikes);
  const oiCalls = oiData?.calls || [];
  const oiPuts = oiData?.puts || [];
  const atmIndex = strikes.indexOf(atm);
  const callSellerStrikes = strikes.slice(
    Math.max(0, atmIndex - 3),
    Math.min(strikes.length, atmIndex + 11)
  );
  const putSellerStrikes = strikes.slice(
    Math.max(0, atmIndex - 10),
    Math.min(strikes.length, atmIndex + 4)
  );
  const callSellerRows = oiCalls.length ? oiCalls : pickRowsByStrikes(calls, callSellerStrikes);
  const putSellerRows = oiPuts.length ? oiPuts : pickRowsByStrikes(puts, putSellerStrikes);

  return {
    spot,
    atm,
    ceTopItmVolume: ceTopItmRows.reduce((sum, row) => sum + row.volume, 0),
    peTopItmVolume: peTopItmRows.reduce((sum, row) => sum + row.volume, 0),
    callSellerOi: num(oiData?.totalCeOI) || callSellerRows.reduce((sum, row) => sum + num(row.oi), 0),
    callSellerChange: callSellerRows.reduce((sum, row) => sum + num(row.oiChange), 0),
    putSellerOi: num(oiData?.totalPeOI) || putSellerRows.reduce((sum, row) => sum + num(row.oi), 0),
    putSellerChange: putSellerRows.reduce((sum, row) => sum + num(row.oiChange), 0),
    callSellerStrikes: callSellerRows.map(row => row.strike),
    putSellerStrikes: putSellerRows.map(row => row.strike),
    ceItmStrikes,
    peItmStrikes,
  };
}

function buildSpotLevels(liveSpot, gap) {
  const rounded = Math.round(num(liveSpot) / gap) * gap;
  return [rounded - gap, rounded, rounded + gap].filter(Boolean);
}

async function postJson(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || data.message || "Dhan fetch failed");
  return data;
}

function Stat({ label, value, color = C.gold }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, background: "#020202" }}>
      <div style={{ fontSize: 8, letterSpacing: 1.4, color: C.dim, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

function SpotPanel({ metrics }) {
  const callChangeColor = metrics.callSellerChange >= 0 ? C.green : C.red;
  const putChangeColor = metrics.putSellerChange >= 0 ? C.green : C.red;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.card }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 2.4, color: C.dim }}>SPOT LEVEL</div>
          <div style={{ fontSize: 21, fontWeight: 900, color: C.gold }}>{fmt(metrics.spot)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, letterSpacing: 2.4, color: C.dim }}>ATM</div>
          <div style={{ fontSize: 17, fontWeight: 900, color: C.blue }}>{fmt(metrics.atm)}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <Stat label="TOTAL VOLUME CE - TOP 10 ITM" value={fmtCr(metrics.ceTopItmVolume)} color={C.green} />
        <Stat label="TOTAL VOLUME PE - TOP 10 ITM" value={fmtCr(metrics.peTopItmVolume)} color={C.red} />
        <Stat label="TOTAL CALL SELLERS" value={fmtCr(metrics.callSellerOi)} color={C.red} />
        <Stat label="CALL SELLERS +/- ADDED/LEFT" value={fmtSignedCr(metrics.callSellerChange)} color={callChangeColor} />
        <Stat label="TOTAL PUT SELLERS" value={fmtCr(metrics.putSellerOi)} color={C.green} />
        <Stat label="PUT SELLERS +/- ADDED/LEFT" value={fmtSignedCr(metrics.putSellerChange)} color={putChangeColor} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 12 }}>
        <div style={{ fontSize: 9, color: C.dim, lineHeight: 1.6 }}>
          CE top ITM strikes: {metrics.ceItmStrikes.join(", ") || "-"}
        </div>
        <div style={{ fontSize: 9, color: C.dim, lineHeight: 1.6 }}>
          PE top ITM strikes: {metrics.peItmStrikes.join(", ") || "-"}
        </div>
        <div style={{ fontSize: 9, color: C.dim, lineHeight: 1.6 }}>
          Call sellers basket: {metrics.callSellerStrikes.join(", ") || "-"}
        </div>
        <div style={{ fontSize: 9, color: C.dim, lineHeight: 1.6 }}>
          Put sellers basket: {metrics.putSellerStrikes.join(", ") || "-"}
        </div>
      </div>
    </div>
  );
}

export default function DhanExpiryWriterMatrix({
  refreshSec = REFRESH_SEC,
  secretClientId = "",
  secretAccessToken = "",
}) {
  const [activeIndex, setActiveIndex] = useState(() => {
    return localStorage.getItem(ACTIVE_INDEX_KEY) || "nifty";
  });
  const [runningByIndex, setRunningByIndex] = useState(readRunningState);
  const [credentials, setCredentials] = useState(() => {
    const saved = readCredentials();
    return {
      clientId: secretClientId || saved.clientId || "",
      accessToken: secretAccessToken || saved.accessToken || "",
    };
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(refreshSec);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const activeConfig = INDEX_TABS.find(tab => tab.id === activeIndex) || INDEX_TABS[0];
  const running = !!runningByIndex[activeIndex];
  const chain = useMemo(() => snapshot?.chain || {}, [snapshot]);
  const liveSpot = num(snapshot?.spot || chain.spot);
  const spotLevels = useMemo(
    () => buildSpotLevels(liveSpot, activeConfig.gap),
    [liveSpot, activeConfig.gap]
  );
  const metrics = useMemo(
    () => spotLevels.map(spot => calculateSpotMetrics(chain, spot, snapshot?.oiBySpot?.[spot])),
    [chain, spotLevels, snapshot]
  );

  useEffect(() => {
    if (secretClientId || secretAccessToken) {
      setCredentials(prev => ({
        clientId: secretClientId || prev.clientId || "",
        accessToken: secretAccessToken || prev.accessToken || "",
      }));
    }
  }, [secretClientId, secretAccessToken]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const loadSecretSettingsCredentials = async () => {
      const res = await fetch(`${API_BASE}/api/login/admin/secret-settings?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;

      const next = {
        clientId: data.settings?.clientId || "",
        accessToken: data.settings?.accessToken || "",
      };

      if (next.clientId || next.accessToken) {
        setCredentials(prev => ({
          clientId: next.clientId || prev.clientId || "",
          accessToken: next.accessToken || prev.accessToken || "",
        }));
        localStorage.setItem(DHAN_CREDENTIALS_KEY, JSON.stringify(next));
      }
    };

    loadSecretSettingsCredentials().catch(() => {});
  }, []);

  const saveCredentials = () => {
    const next = {
      clientId: String(credentials.clientId || "").trim(),
      accessToken: String(credentials.accessToken || "").trim(),
    };
    setCredentials(next);
    localStorage.setItem(DHAN_CREDENTIALS_KEY, JSON.stringify(next));
    setShowCredentials(false);
  };

  const changeActiveIndex = (nextIndex) => {
    setActiveIndex(nextIndex);
    localStorage.setItem(ACTIVE_INDEX_KEY, nextIndex);
  };

  const loadData = async () => {
    const cleanCredentials = {
      clientId: String(credentials.clientId || "").trim(),
      accessToken: String(credentials.accessToken || "").trim(),
    };

    if (!cleanCredentials.clientId || !cleanCredentials.accessToken) {
      setError("Missing Dhan client ID or access token");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await postJson(`/api/dhan-option-decay/${activeIndex}`, cleanCredentials);
      const expiry = data.expiry;
      const spot = num(data.spot);
      const chainData = data.chain || {};

      if (!expiry) throw new Error("No Dhan expiry found");
      if (!spot) throw new Error("Dhan live spot not found");

      setSnapshot({
        source: "DHAN",
        updatedAt: data.updatedAt || new Date().toISOString(),
        expiry,
        spot,
        chain: {
          spot,
          expiry,
          calls: chainData.calls || [],
          puts: chainData.puts || [],
        },
        oiBySpot: {},
      });
      setCountdown(Math.max(1, Number(refreshSec || REFRESH_SEC)));
    } catch (err) {
      const message = err.message || "Dhan writer matrix fetch failed";
      setError(message);

    } finally {
      setLoading(false);
    }
  };

  const toggleRunning = () => {
    setRunningByIndex(prev => {
      const next = { ...prev, [activeIndex]: !prev[activeIndex] };
      localStorage.setItem(RUNNING_KEY, JSON.stringify(next));
      return next;
    });
  };

  const stopTimers = () => {
    clearInterval(intervalRef.current);
    clearInterval(countdownRef.current);
    intervalRef.current = null;
    countdownRef.current = null;
  };

  useEffect(() => {
    stopTimers();

    if (!running) return undefined;

    loadData();

    intervalRef.current = setInterval(loadData, Math.max(1, Number(refreshSec || REFRESH_SEC)) * 1000);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => prev <= 1 ? Math.max(1, Number(refreshSec || REFRESH_SEC)) : prev - 1);
    }, 1000);

    return stopTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, activeIndex, refreshSec, credentials.clientId, credentials.accessToken]);

  useEffect(() => {
    if (credentials.clientId && credentials.accessToken) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, credentials.clientId, credentials.accessToken]);

  return (
    <div style={{ color: C.gold }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        {INDEX_TABS.map(tab => {
          const active = activeIndex === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => changeActiveIndex(tab.id)}
              style={{
                minWidth: 150,
                padding: "16px 22px",
                border: `1px solid ${active ? "#00b37a" : C.border}`,
                background: active ? "#006b49" : "#050505",
                color: active ? "#ffffff" : C.gold,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 900,
                letterSpacing: 3,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, background: C.panel, marginBottom: 18 }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 4, color: C.dim }}>{activeConfig.label} DHAN LIVE SPOT</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.gold }}>{liveSpot ? fmt(liveSpot) : "-"}</div>
              <div style={{ fontSize: 11, color: C.blue }}>DHAN</div>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, position: "relative" }}>
            <button
              type="button"
              onClick={() => setShowCredentials(prev => !prev)}
              style={{
                border: `1px solid ${C.border}`,
                background: "#050505",
                color: C.gold,
                borderRadius: 6,
                padding: "12px 18px",
                fontWeight: 900,
                letterSpacing: 2,
              }}
            >
              CREDENTIALS
            </button>

            {showCredentials && (
              <div style={{
                position: "absolute",
                right: 0,
                top: 48,
                zIndex: 20,
                width: 640,
                maxWidth: "90vw",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: 12,
                background: "#020202",
                boxShadow: "0 18px 50px rgba(0,0,0,0.7)",
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 10 }}>
                  <input
                    value={credentials.clientId}
                    onChange={event => setCredentials(prev => ({ ...prev, clientId: event.target.value }))}
                    placeholder="Dhan Client ID"
                    style={{ background: "#050505", border: `1px solid ${C.border}`, color: C.gold, borderRadius: 6, padding: "10px 12px" }}
                  />
                  <input
                    value={credentials.accessToken}
                    onChange={event => setCredentials(prev => ({ ...prev, accessToken: event.target.value }))}
                    placeholder="Dhan Access Token"
                    type="password"
                    style={{ background: "#050505", border: `1px solid ${C.border}`, color: C.gold, borderRadius: 6, padding: "10px 12px" }}
                  />
                  <button
                    type="button"
                    onClick={saveCredentials}
                    style={{ background: "#006b49", color: "#fff", border: "1px solid #00b37a", borderRadius: 6, padding: "10px 16px", fontWeight: 900 }}
                  >
                    SAVE
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={toggleRunning}
              style={{
                minWidth: 120,
                border: `1px solid ${running ? "#ff5068" : "#00b37a"}`,
                background: running ? "#5b0714" : "#006b49",
                color: "#fff",
                borderRadius: 6,
                padding: "12px 18px",
                fontWeight: 900,
                letterSpacing: 2,
              }}
            >
              {running ? "STOP" : "START"}
            </button>
            <button
              type="button"
              onClick={loadData}
              style={{
                border: `1px solid ${C.border}`,
                background: "#050505",
                color: C.gold,
                borderRadius: 6,
                padding: "12px 18px",
                fontWeight: 900,
                letterSpacing: 2,
              }}
            >
              REFRESH
            </button>
          </div>
        </div>

        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 18, fontSize: 11, letterSpacing: 2 }}>
          <span style={{ color: running ? C.green : C.dim }}>{running ? `LIVE - ${countdown}s` : "STOPPED"}</span>
          <span>CLIENT: {credentials.clientId || "-"}</span>
          <span>GAP: {activeConfig.gap}</span>
          <span>EXPIRY: {snapshot?.expiry || "-"}</span>
          <span>UPDATED: {snapshot?.updatedAt ? new Date(snapshot.updatedAt).toLocaleTimeString("en-IN") : "-"}</span>
          {loading && <span style={{ color: C.blue }}>FETCHING...</span>}
          {error && <span style={{ color: C.red }}>{error}</span>}
        </div>
      </div>

      <div style={{ display: "grid", gap: 16 }}>
        {metrics.map(item => (
          <SpotPanel key={item.spot} metrics={item} />
        ))}
      </div>
    </div>
  );
}
